import React, { useState } from 'react';

const MicIcon = () => (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
);

const StethoscopeIcon = () => (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        <circle cx="12" cy="17" r="2" strokeWidth={1.75} />
    </svg>
);

const GamesModal = ({ folder, onClose, onLaunchGame, onLaunchAnamnesisNemesis }) => {
    const [launching, setLaunching] = useState(null);

    const handleVerbatim = async () => {
        setLaunching('verbatim');
        try { await Promise.resolve(onLaunchGame(folder)); }
        finally { setTimeout(() => setLaunching(null), 1500); }
    };

    const handleAnamnesis = () => {
        setLaunching('anamnesis');
        onLaunchAnamnesisNemesis(folder);
    };

    const games = [
        {
            id: 'verbatim',
            Icon: MicIcon,
            name: 'Verbatim Master',
            badge: 'Voice Scored',
            badgeClass: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/20',
            description: 'Speak your answer aloud — scored in real time. Builds deep recall through active production, not passive recognition.',
            meta: [
                { label: 'Difficulty', value: 'Hard', color: 'text-rose-300' },
                { label: 'Time', value: '5–15 min', color: 'text-slate-400' },
                { label: 'Mode', value: 'Voice', color: 'text-amber-300' },
            ],
            buttonLabel: 'Play Verbatim Master',
            buttonClass: 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white shadow-md shadow-amber-500/20',
            iconBg: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/20',
            onClick: handleVerbatim,
        },
        {
            id: 'anamnesis',
            Icon: StethoscopeIcon,
            name: 'Anamnesis Nemesis',
            badge: 'Clinical Sim',
            badgeClass: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/20',
            description: 'Step into a simulated patient encounter and navigate a clinical case using your knowledge. Builds diagnostic reasoning.',
            meta: [
                { label: 'Difficulty', value: 'Expert', color: 'text-amber-300' },
                { label: 'Time', value: '10–20 min', color: 'text-slate-400' },
                { label: 'Mode', value: 'Scenario', color: 'text-emerald-300' },
            ],
            buttonLabel: 'Start Case',
            buttonClass: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md shadow-emerald-500/20',
            iconBg: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/20',
            onClick: handleAnamnesis,
        },
    ];

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-950/70 backdrop-blur-sm px-0 sm:px-4"
            onClick={onClose}
        >
            <div
                className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl border border-white/10 bg-[var(--bg-surface)] shadow-2xl shadow-black/50 pb-safe"
                onClick={e => e.stopPropagation()}
            >
                {/* Drag handle (mobile) */}
                <div className="flex justify-center pt-3 pb-1 sm:hidden">
                    <div className="h-1 w-10 rounded-full bg-white/20" />
                </div>

                {/* Header */}
                <div className="flex items-start justify-between px-5 pt-4 pb-3 sm:pt-5">
                    <div>
                        <h2 className="text-base font-semibold text-white tracking-tight">Games</h2>
                        <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[260px]">{folder.name}</p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Game cards */}
                <div className="px-4 pb-5 grid gap-3">
                    {games.map(({ id, Icon, name, badge, badgeClass, description, meta, buttonLabel, buttonClass, iconBg, onClick }) => {
                        const isLaunching = launching === id;
                        return (
                            <div
                                key={id}
                                className="rounded-xl border border-white/8 bg-slate-900/50 p-4"
                            >
                                <div className="flex items-start gap-3">
                                    {/* Icon */}
                                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
                                        <Icon />
                                    </div>
                                    {/* Title + badge */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-white">{name}</span>
                                            <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${badgeClass}`}>{badge}</span>
                                        </div>
                                        <p className="mt-1 text-xs text-slate-400 leading-relaxed">{description}</p>
                                    </div>
                                </div>

                                {/* Meta row */}
                                <div className="mt-3 flex items-center gap-4">
                                    {meta.map(({ label, value, color }) => (
                                        <div key={label} className="text-[11px]">
                                            <span className="text-slate-600">{label}: </span>
                                            <span className={`font-medium ${color}`}>{value}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA */}
                                <button
                                    disabled={!!launching}
                                    onClick={onClick}
                                    className={`mt-3 w-full rounded-xl py-2.5 text-sm font-medium transition disabled:opacity-60 disabled:cursor-not-allowed ${buttonClass}`}
                                >
                                    {isLaunching ? (
                                        <span className="inline-flex items-center justify-center gap-2">
                                            <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                            Starting…
                                        </span>
                                    ) : buttonLabel}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Safe area spacer on mobile */}
                <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />
            </div>
        </div>
    );
};

export default GamesModal;
